import { css } from '@styled-system/css';
import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button';
import { FormField } from '@/shared/components/ui/FormField';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { useGetSellerByLoginQuery } from '@/shared/hooks/queries/generated';

// ============================================================================
// Création d'une veille VENDEUR : URL de profil (porte l'identifiant, aucune
// résolution nécessaire) ou pseudo, résolu par getSellerByLogin — EN BASE
// RECOP uniquement : un vendeur jamais croisé par l'ingester est introuvable
// même s'il existe sur Vinted (proposer le repli par URL).
// ============================================================================

export interface CreateVendorVeilleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, sellerVintedId: string) => Promise<void> | void;
}

const body = css({ display: 'flex', flexDirection: 'column', gap: '5', paddingTop: '4' });

const errorText = css({ fontSize: 'caption', color: 'error.default' });

export const CreateVendorVeilleModal: FC<CreateVendorVeilleModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [vendor, setVendor] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setVendor('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    const raw = vendor.trim().replace(/^@/, '');
    if (!raw) {
      setError(t('veille.vendorRequired'));
      return;
    }

    // URL de profil : l'identifiant numérique est dedans, aucune résolution
    const memberMatch = /vinted\.[a-z.]+\/member\/(\d+)/i.exec(raw);
    let sellerVintedId = memberMatch?.[1] ?? null;
    let login = raw;

    if (!sellerVintedId) {
      if (/^\d+$/.test(raw)) {
        sellerVintedId = raw;
      } else {
        // Pseudo → résolution en base Recop
        const result = await useGetSellerByLoginQuery.fetcher({ login: raw })();
        if (!result.getSellerByLogin) {
          setError(t('veille.vendorUnknown'));
          return;
        }
        sellerVintedId = result.getSellerByLogin.sellerVintedId;
        login = result.getSellerByLogin.login;
      }
    }

    await onSubmit(name.trim() || `@${login}`, sellerVintedId);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('veille.newVendor')}
      width="440px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" handleClick={handleSubmit} showLoader>
            {t('veille.start')}
          </Button>
        </>
      }
    >
      <div className={body}>
        <FormField label={t('veille.vendorLabel')} hint={t('veille.vendorHint')}>
          <Input
            autoFocus
            value={vendor}
            placeholder={t('veille.vendorPlaceholder')}
            state={error ? 'error' : 'default'}
            onChange={(event) => {
              setVendor(event.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleSubmit();
              }
            }}
          />
        </FormField>
        <FormField label={t('veille.nameLabel')} hint={t('veille.nameHint')}>
          <Input value={name} placeholder={t('veille.namePlaceholder')} onChange={(event) => setName(event.target.value)} />
        </FormField>
        {error && <span className={errorText}>{error}</span>}
      </div>
    </Modal>
  );
};
